/**
 * How the plants look. There are three looks (PlantLook), each one way of
 * drawing the whole set so the flora reads as one world, and every kind is a
 * block in each: a rock grows one look (see plantLook in shared/world/plants.ts).
 * TileAtlas paints every plant block from here, and preview.html shows them
 * side by side ("Plant designs").
 *
 * Plants aren't single tiles: growFlora (shared/world/plants.ts) lays each
 * one as strokes and blobs of one tile, a variant picked per position (see
 * GridMesh). Reeds are four to seven stalks of three to six tiles side by
 * side, shrubs a round blob, vines and roots hang from a cave roof, frost
 * flowers stand two or three tiles tall. So every tile here is a segment:
 * stems run top to bottom so a column joins up, hanging kinds hang from the
 * tile's top, and the three variants of a tile differ in where their leaves
 * and heads sit so a stack doesn't repeat.
 */
import type { PlantLook } from "@dyson/shared";
import { TILE_PX, disc, fill, hash, line, noise, oval, path, pixels, poly, tone, type Ctx } from "./paint";

const S = TILE_PX;
type Ramp = readonly string[];

export type PlantFamily = PlantLook;

export const PLANT_FAMILIES: Record<PlantFamily, { name: string; blurb: string }> = {
  natural: { name: "Natural", blurb: "Soft and rounded in leaf and earth colours, shaded like the ships' timber: plants a field guide would draw." },
  otherworld: { name: "Otherworld", blurb: "Starbark indigo and teal, jointed stems, glowing nodes and beads, odd geometry: flora from somewhere else." },
  woodcut: { name: "Woodcut", blurb: "Bold flat shapes in three tones with ink lines, big leaves and chunky caps: reads at a glance from across the sky." },
};

export interface PlantDesign {
  family: PlantFamily;
  blurb: string;
  /** Paint variant `v` (0..2) of the tile, upright, the ground along its bottom (or the roof along its top, for a hanging kind). */
  paint: (ctx: Ctx, v: number) => void;
}


export interface PlantGroup {
  id: string;
  title: string;
  note: string;
  /** The block it's painted for, by its name in the registry without the look (NATURAL_REEDS is "REEDS"). */
  key: string;
  designs: PlantDesign[];
}

// ----- palettes

const GREEN = ["#1b3a1a", "#2f5f28", "#468a36", "#63aa45", "#8fcf5e", "#c4ea8a"] as const;
const BUSH = ["#1f3a1c", "#2f5a28", "#447a36", "#5e9a46", "#80b858", "#a8d070"] as const;
const EARTH = ["#2e1f12", "#4a3220", "#67472c", "#86613c", "#a37c50", "#c49a68"] as const;
const INDIGO = ["#151026", "#1f1838", "#2c2448", "#3a3060", "#4a3e78", "#6a5aa8", "#9c8cd8"] as const;
const TEAL = ["#0c2e2c", "#14423f", "#1d5e5a", "#2a8a7e", "#38a896", "#7fe0c8", "#d8fff4"] as const;
const ICE = ["#3f7aa8", "#5f93b8", "#8fbad2", "#b4d6e8", "#d7ecf6", "#f2fbff"] as const;
const VIOLET = ["#241547", "#3a2270", "#5a38a8", "#8a68e0", "#b9a0ff", "#e6dcff"] as const;
const CAPBLUE = ["#1c5a68", "#2a8090", "#3aa8b8", "#6ad0dc", "#b8f4f8"] as const;
const CREAM = ["#9ab0a0", "#c8d8c8", "#e6ece0"] as const;
/** Woodcut: three flat tones a plant, and ink. */
const CUT = ["#1e4a22", "#3f8a36", "#8cc850"] as const;
const CUT_BROWN = ["#3a2616", "#6b4a2c", "#a37c50"] as const;
const CUT_BLUE = ["#1d3f7a", "#3b86c8", "#a8dcff"] as const;
const CUT_VIOLET = ["#2d1a5e", "#6a48c0", "#c4b0ff"] as const;
const INK = "#120f10";
const GLOW_PX = "#f4fffb";

// ----- parts

/** A stem `w` wide standing at x from y0 down `h`: lit on its left, dark on its right. */
function stem(ctx: Ctx, x: number, y0: number, h: number, w: number, ramp: Ramp): void {
  pixels(ctx, w, h, (px, py) => tone(ramp, 0.88 - ((px + 0.5) / w) * 0.7, px, py, 0.3), x, y0);
}

/** A strand from (x, 0) to (x, S) wavering `wave` pixels either way (the same x at both ends, so a column of tiles joins up): the points for path(). */
function strand(x: number, wave: number, phase: number, bends = 2): number[] {
  const pts: number[] = [];
  for (let y = 0; y <= S; y += 2) pts.push(x + Math.sin((y / S) * Math.PI * bends + phase) * wave * Math.sin((y / S) * Math.PI), y);
  return pts;
}

/** Where a strand is at height y. */
function strandX(pts: readonly number[], y: number): number {
  return pts[Math.min(pts.length - 2, Math.round(y / 2) * 2)]!;
}

/** A leaf: an oval of `ramp` with a lighter upper half and a dark midrib. */
function leaf(ctx: Ctx, cx: number, cy: number, rx: number, ry: number, a: number, ramp: Ramp): void {
  oval(ctx, ramp[1]!, cx, cy, rx, ry, a);
  oval(ctx, ramp[3]!, cx - Math.sin(a) * ry * 0.35, cy - Math.cos(a) * ry * 0.35, rx * 0.8, ry * 0.55, a);
  line(ctx, ramp[0]!, cx - Math.cos(a) * rx * 0.8, cy - Math.sin(a) * rx * 0.8, cx + Math.cos(a) * rx * 0.8, cy + Math.sin(a) * rx * 0.8, 1);
}

/** A flat woodcut leaf: ink round a two-tone oval, with an ink midrib. */
function cutLeaf(ctx: Ctx, cx: number, cy: number, rx: number, ry: number, a: number, ramp: Ramp): void {
  oval(ctx, INK, cx, cy, rx + 1, ry + 1, a);
  oval(ctx, ramp[1]!, cx, cy, rx, ry, a);
  oval(ctx, ramp[2]!, cx - Math.sin(a) * ry * 0.4, cy - Math.cos(a) * ry * 0.4, rx * 0.7, ry * 0.4, a);
  line(ctx, INK, cx - Math.cos(a) * rx * 0.9, cy - Math.sin(a) * rx * 0.9, cx + Math.cos(a) * rx * 0.9, cy + Math.sin(a) * rx * 0.9, 1);
}

/** A mushroom cap: the top of an ellipse `r` across at (cx, cy), `flat` as tall as wide, shaded lit from the upper left, gills in a row under its rim. */
function cap(ctx: Ctx, cx: number, cy: number, r: number, flat: number, ramp: Ramp, gill: string): void {
  const h = Math.ceil(r * flat);
  pixels(
    ctx,
    Math.ceil(r * 2),
    h + 1,
    (px, py) => {
      const dx = (px + 0.5 - r) / r;
      const dy = (py + 0.5 - h) / h;
      if (dx * dx + dy * dy > 1) return null;
      if (py === h) return (px & 1) === 0 ? gill : ramp[0]!;
      return tone(ramp, 0.72 - dx * 0.25 + dy * 0.4, px, py);
    },
    Math.round(cx - r),
    Math.round(cy - h),
  );
}

/** A crystal shard: a four-cornered facet from `top` to `bottom`, `half` wide at `waist` of the way down, dark with a lit left face and a bright edge. */
function shard(ctx: Ctx, tx: number, ty: number, bx: number, by: number, half: number, waist: number, ramp: Ramp): void {
  const wx = tx + (bx - tx) * waist;
  const wy = ty + (by - ty) * waist;
  const nx = -(by - ty);
  const ny = bx - tx;
  const len = Math.hypot(nx, ny) || 1;
  const ox = (nx / len) * half;
  const oy = (ny / len) * half;
  poly(ctx, ramp[1]!, [tx, ty, wx + ox, wy + oy, bx, by, wx - ox, wy - oy]);
  poly(ctx, ramp[2]!, [tx, ty, wx - ox * 0.1, wy - oy * 0.1, bx, by, wx - ox, wy - oy]);
  poly(ctx, ramp[3]!, [tx, ty, wx - ox * 0.15, wy - oy * 0.15, bx - (bx - wx) * 0.4, by - (by - wy) * 0.4, wx - ox * 0.6, wy - oy * 0.6]);
  line(ctx, ramp[4]!, tx, ty, wx - ox * 0.9, wy - oy * 0.9, 1);
}

/** Cut a round bite out of what's drawn: the ragged edge of a shrub tile, so a blob of them isn't a square. */
function bite(ctx: Ctx, cx: number, cy: number, r: number): void {
  ctx.save();
  ctx.globalCompositeOperation = "destination-out";
  disc(ctx, "#000", cx, cy, r);
  ctx.restore();
}

/** A small glowing point: a bright pixel pair with a tinted halo. */
function spark(ctx: Ctx, x: number, y: number, halo: string): void {
  fill(ctx, halo, x - 1, y, 3, 1);
  fill(ctx, halo, x, y - 1, 1, 3);
  fill(ctx, GLOW_PX, x, y, 1, 1);
}

/** Thin, flat ink line along a stem's side. */
const ink = (ctx: Ctx, x0: number, y0: number, x1: number, y1: number, w = 1) => line(ctx, INK, x0, y0, x1, y1, w);

// ----- the designs

const reeds: PlantDesign[] = [
  {
    family: "natural",
    blurb: "Three round stalks the height of the tile, grass blades curling off them. One variant carries a brown cattail head.",
    paint: (ctx, v) => {
      const xs = [[5, 15, 26], [8, 18, 27], [4, 13, 23]][v]!;
      xs.forEach((x, i) => {
        stem(ctx, x, 0, S, 2, GREEN);
        const side = (i + v) % 2 ? 1 : -1;
        line(ctx, GREEN[3], x + 1, 24 - i * 4, x + side * 6, 10 - i * 3, 1.5);
        line(ctx, GREEN[4], x + 1, 30, x - side * 4, 20, 1);
        if (v === 2 && i === 1) {
          fill(ctx, EARTH[1], x - 1, 3, 4, 10);
          fill(ctx, EARTH[3], x - 1, 3, 2, 10);
          fill(ctx, EARTH[4], x - 1, 4, 1, 8);
          fill(ctx, GREEN[5], x, 0, 1, 3);
        }
      });
    },
  },
  {
    family: "otherworld",
    blurb: "Jointed indigo stems like horsetail, a pale ring at each node with a teal spike and a glowing bead, the joints staggered so a column doesn't line up.",
    paint: (ctx, v) => {
      const xs = [[6, 16, 25], [9, 19, 28], [3, 14, 23]][v]!;
      xs.forEach((x, i) => {
        stem(ctx, x, 0, S, 3, INDIGO);
        for (let y = 4 + ((v * 3 + i * 4) % 9); y < S; y += 9) {
          fill(ctx, INDIGO[5], x, y, 3, 1);
          fill(ctx, INDIGO[0], x, y + 1, 3, 1);
          const side = (i + Math.floor(y / 9)) % 2 ? 1 : -1;
          line(ctx, TEAL[4], x + 1.5, y, x + 1.5 + side * 5, y - 5, 1);
          if ((y + i) % 2 === 0) spark(ctx, x + (side > 0 ? 3 : -1), y, TEAL[3]);
        }
      });
      if (v === 1) spark(ctx, xs[0]! + 1, 2, TEAL[4]);
    },
  },
  {
    family: "woodcut",
    blurb: "Broad flat blades, three tones with an ink edge, leaning a little each way. One variant tops a blade with an ink-black seed head.",
    paint: (ctx, v) => {
      const xs = [[7, 19, 28], [5, 16, 26], [10, 22, 2]][v]!;
      const leans = [1, -1, 2];
      xs.forEach((x, i) => {
        const l = leans[(i + v) % 3]!;
        // A leaf curling off the stalk: ink first, then the flat blade over it.
        const side = (i + v) % 2 ? 1 : -1;
        const ly = 8 + ((i * 9 + v * 5) % 14);
        const curl = [x + l * (1 - ly / S), ly + 8, x + side * 4 + l * (1 - ly / S), ly + 3, x + side * 9, ly - 4, x + side * 11, ly - 9];
        path(ctx, INK, curl, 4);
        path(ctx, CUT[1], curl, 2.2);
        path(ctx, CUT[2], curl.map((p, k) => (k % 2 ? p - 1 : p)), 1);
        line(ctx, INK, x - 2.5, S, x - 2.5 + l, 0, 1);
        line(ctx, CUT[1], x, S, x + l, 0, 4);
        line(ctx, CUT[2], x - 1.2, S, x - 1.2 + l, 0, 1.2);
        ink(ctx, x + 2, S, x + 2 + l, 0);
        if (v === 2 && i === 1) {
          fill(ctx, CUT_BROWN[0], x - 1, 3, 5, 10);
          fill(ctx, CUT_BROWN[1], x, 4, 1, 8);
          ink(ctx, x - 1.5, 3, x - 1.5, 13);
          ink(ctx, x + 3.5, 3, x + 3.5, 13);
        }
      });
    },
  },
];

const shrub: PlantDesign[] = [
  {
    family: "natural",
    blurb: "Round clumps of leaves shaded as little domes, packed edge to edge over a dark interior, with a couple of small bites from the edges so a blob of tiles has a leafy outline. One variant has red berries.",
    paint: (ctx, v) => {
      fill(ctx, BUSH[0]);
      const clumps: [number, number, number][] = [];
      for (let i = 0; i < 9; i++) clumps.push([(i % 3) * 11 + 5 + (hash(i, v, 301) - 0.5) * 6, Math.floor(i / 3) * 11 + 5 + (hash(i, v, 302) - 0.5) * 6, 5.5 + hash(i, v, 303) * 1.5]);
      clumps.sort((a, b) => a[1] - b[1]);
      pixels(ctx, S, S, (x, y) => {
        for (let n = clumps.length - 1; n >= 0; n--) {
          const [cx, cy, r] = clumps[n]!;
          const dx = (x + 0.5 - cx) / r;
          const dy = (y + 0.5 - cy) / r;
          const d2 = dx * dx + dy * dy + (noise(x, y, 3, 310 + n) - 0.5) * 0.3;
          if (d2 > 1) continue;
          if (d2 > 0.6 && hash(x, y, 320 + n) < 0.25) return BUSH[0];
          return tone(BUSH, 0.45 - dx * 0.25 - dy * 0.4 + Math.sqrt(Math.max(0, 1 - d2)) * 0.25, x, y);
        }
        return null;
      });
      for (const [cx, cy, r] of [[[0, 0, 4], [S, 14, 3]], [[S, 0, 4], [8, S, 3]], [[14, 0, 3], [0, 20, 4]]][v]!) bite(ctx, cx!, cy!, r!);
      if (v === 2) for (const [x, y] of [[9, 12], [22, 9], [17, 22]]) {
        disc(ctx, "#c0303a", x!, y!, 1.6);
        fill(ctx, "#ff9a9a", x! - 1, y! - 1, 1, 1);
      }
    },
  },
  {
    family: "otherworld",
    blurb: "Overlapping teal scales like a fern's fronds seen end on, each row shading over the next, with pale spores drifting on one variant.",
    paint: (ctx, v) => {
      pixels(ctx, S, S, (x, y) => {
        const band = Math.floor((y + v * 3) / 8);
        const off = (band % 2) * 6;
        const cx = Math.floor((x + off) / 12) * 12 - off + 6;
        const by = (y + v * 3) % 8;
        const d = Math.hypot((x + 0.5 - cx) / 7.5, (by - 8) / 8);
        if (d > 0.95) return TEAL[0];
        return tone(TEAL, 0.8 - (by / 8) * 0.45 - (Math.abs(x + 0.5 - cx) / 7.5) * 0.25, x, y);
      });
      for (const [cx, cy, r] of [[[0, 0, 4], [S, 12, 3]], [[S, 0, 3], [8, S, 4]], [[12, 0, 3], [0, 20, 4]]][v]!) bite(ctx, cx!, cy!, r!);
      if (v === 1) for (const [x, y] of [[8, 6], [24, 14], [14, 24]]) spark(ctx, x!, y!, TEAL[4]);
    },
  },
  {
    family: "woodcut",
    blurb: "Flat mid-green with big simple leaves drawn over it in ink, and pointed notches cut from the edges so the blob's outline is all leaf tips.",
    paint: (ctx, v) => {
      fill(ctx, CUT[0]);
      pixels(ctx, S, S, (x, y) => (noise(x, y, 5, 230 + v) > 0.55 ? CUT[1] : null));
      const leaves = [[[7, 8, 0.6], [23, 6, -0.4], [12, 22, -0.9], [26, 22, 0.9]], [[10, 6, -0.7], [25, 12, 0.5], [8, 24, 0.9], [22, 26, -0.6]], [[6, 14, 1.1], [18, 8, 0.2], [26, 20, -1.1], [12, 27, 0.4]]][v]!;
      for (const [x, y, a] of leaves) cutLeaf(ctx, x!, y!, 6.5, 3.5, a!, CUT);
      ctx.save();
      ctx.globalCompositeOperation = "destination-out";
      for (const [x, y, dx, dy] of [[[0, 0, 5, 7], [S, 10, -6, 5]], [[S, 0, -7, 5], [8, S, 6, -5]], [[14, 0, 6, 5], [0, 22, 6, 5]]][v]!) {
        poly(ctx, "#000", [x!, y!, x! + dx!, y!, x!, y! + dy!]);
      }
      ctx.restore();
    },
  },
];

const glowFungus: PlantDesign[] = [
  {
    family: "natural",
    blurb: "One broad cap and two small on cream stalks, shaded blue-green with pale spots and gills under the rim; the big cap moves between variants.",
    paint: (ctx, v) => {
      const big = [14, 17, 12][v]!;
      const smalls = [[[5, 22], [27, 24]], [[6, 25], [26, 21]], [[24, 23], [28, 27]]][v]!;
      for (const [x, y] of smalls) {
        stem(ctx, x! - 1, y!, S - y!, 2, CREAM);
        cap(ctx, x!, y!, 4, 0.7, CAPBLUE, "#164a64");
      }
      stem(ctx, big - 2, 14, S - 14, 4, CREAM);
      cap(ctx, big, 15, 10, 0.6, CAPBLUE, "#164a64");
      for (const [x, y] of [[big - 5, 11], [big + 3, 9]]) fill(ctx, CAPBLUE[4], x!, y!, 2, 1);
      spark(ctx, big - 1, 12, CAPBLUE[3]);
    },
  },
  {
    family: "otherworld",
    blurb: "Tall thin stalks with drooping bell caps, a bright teal rim at each mouth, threads of light hanging from the rims.",
    paint: (ctx, v) => {
      const bells = [[[10, 8], [23, 14]], [[16, 6], [6, 18]], [[8, 12], [25, 7]]][v]!;
      for (const [x, y] of bells) {
        stem(ctx, x! - 1, y!, S - y!, 2, INDIGO);
        oval(ctx, TEAL[2], x!, y! + 1, 4, 2.5);
        poly(ctx, TEAL[2], [x! - 4, y! + 1, x! + 4, y! + 1, x! + 7, y! + 10, x! - 7, y! + 10]);
        poly(ctx, TEAL[3], [x! - 4, y! + 1, x! - 1, y!, x! - 1, y! + 10, x! - 7, y! + 10]);
        oval(ctx, TEAL[4], x! - 1.5, y! + 1, 1.5, 1);
        fill(ctx, TEAL[1], x! - 6, y! + 9, 12, 1);
        fill(ctx, TEAL[5], x! - 7, y! + 10, 14, 1);
        line(ctx, TEAL[4], x! - 4, y! + 11, x! - 4, y! + 15, 1);
        line(ctx, TEAL[4], x! + 3, y! + 11, x! + 3, y! + 14, 1);
        spark(ctx, x! - 4, y! + 16, TEAL[3]);
      }
    },
  },
  {
    family: "woodcut",
    blurb: "Chunky flat toadstools: deep blue caps with a pale rim band, white dots and ink edges, on thick pale stalks.",
    paint: (ctx, v) => {
      const caps = [[[13, 14, 10], [26, 24, 5]], [[18, 13, 10], [6, 23, 5]], [[10, 16, 9], [24, 20, 6]]][v]!;
      for (const [x, y, r] of caps) {
        const w = Math.max(3, Math.round(r! / 2));
        fill(ctx, INK, x! - w / 2 - 1, y!, w + 2, S - y!);
        fill(ctx, "#e9e3d4", x! - w / 2, y!, w, S - y!);
        fill(ctx, "#b8b0a0", x! + w / 2 - 1, y!, 1, S - y!);
        oval(ctx, INK, x!, y!, r! + 1, r! * 0.65 + 1);
        oval(ctx, CUT_BLUE[0], x!, y!, r!, r! * 0.65);
        oval(ctx, CUT_BLUE[1], x! - 0.5, y! - 1, r! * 0.8, r! * 0.4);
        fill(ctx, INK, x! - r!, y!, r! * 2 + 1, 1);
        fill(ctx, CUT_BLUE[2], x! - r! + 1, y! - 1, r! * 2 - 1, 1);
        for (const [dx, dy] of [[-r! * 0.5, -r! * 0.2], [r! * 0.3, -r! * 0.35]]) fill(ctx, CUT_BLUE[2], Math.round(x! + dx!), Math.round(y! + dy!), 2, 1);
      }
    },
  },
];

const frostFlower: PlantDesign[] = [
  {
    family: "natural",
    blurb: "A frosty green stem the height of the tile with a six-petal ice bloom on it and a leaf below; stacked, the tiles make a spike of blooms at staggered heights.",
    paint: (ctx, v) => {
      const fy = [12, 8, 16][v]!;
      stem(ctx, 15, 0, S, 2, ["#3f7a6a", "#5f9f8c", "#9fd0c4"]);
      const side = v === 1 ? 1 : -1;
      leaf(ctx, 16 + side * 5, fy + 12, 5, 2, side * -0.5, ["#2f5f5a", "#4f8f82", "#6fb0a0", "#a8d8cc"]);
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2 + v * 0.3;
        oval(ctx, ICE[3], 16 + Math.cos(a) * 6, fy + Math.sin(a) * 6, 4.5, 2.5, a);
        oval(ctx, ICE[5], 16 + Math.cos(a) * 5.5, fy + Math.sin(a) * 5.5, 3, 1.4, a);
      }
      disc(ctx, "#4fa8e8", 16, fy, 3);
      disc(ctx, "#9fe0ff", 15.5, fy - 0.5, 1.8);
    },
  },
  {
    family: "otherworld",
    blurb: "Sharp ice prisms radiating from a glowing core on a dark stem veined with teal, with frost crystals clinging to the stem.",
    paint: (ctx, v) => {
      const fy = [11, 7, 15][v]!;
      stem(ctx, 15, 0, S, 2, INDIGO);
      pixels(ctx, 1, S, (_, y) => (y % 3 ? TEAL[4] : null), 15, 0);
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2 - Math.PI / 2 + v * 0.35;
        const tx = 16 + Math.cos(a) * 9;
        const ty = fy + Math.sin(a) * 9;
        const nx = -Math.sin(a) * 2;
        const ny = Math.cos(a) * 2;
        poly(ctx, ICE[2], [16 + nx, fy + ny, tx, ty, 16 - nx, fy - ny]);
        poly(ctx, ICE[4], [16 + nx, fy + ny, tx, ty, 16, fy]);
        line(ctx, ICE[5], 16 + nx, fy + ny, tx, ty, 1);
      }
      disc(ctx, ICE[1], 16, fy, 3);
      disc(ctx, GLOW_PX, 16, fy, 1.6);
      for (const y of [fy + 13, fy + 18]) {
        fill(ctx, ICE[4], 13, y, 3, 1);
        fill(ctx, ICE[4], 17, y + 2, 3, 1);
      }
    },
  },
  {
    family: "woodcut",
    blurb: "A thick dark-teal stem and a snowflake bloom in white and mid-blue with ink arms, a flat teardrop leaf at its foot.",
    paint: (ctx, v) => {
      const fy = [12, 8, 16][v]!;
      fill(ctx, INK, 14, 0, 5, S);
      fill(ctx, "#2f6a6a", 15, 0, 3, S);
      fill(ctx, "#5fa0a0", 15, 0, 1, S);
      const side = v === 1 ? 1 : -1;
      poly(ctx, INK, [16, fy + 12, 16 + side * 9, fy + 9, 16 + side * 4, fy + 16]);
      poly(ctx, "#2f6a6a", [16, fy + 12, 16 + side * 7, fy + 10, 16 + side * 4, fy + 14]);
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI;
        line(ctx, INK, 16 - Math.cos(a) * 8, fy - Math.sin(a) * 8, 16 + Math.cos(a) * 8, fy + Math.sin(a) * 8, i % 2 ? 2.5 : 3.5);
      }
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI;
        line(ctx, i % 2 ? CUT_BLUE[2] : "#ffffff", 16 - Math.cos(a) * 7, fy - Math.sin(a) * 7, 16 + Math.cos(a) * 7, fy + Math.sin(a) * 7, i % 2 ? 1 : 1.6);
      }
      disc(ctx, INK, 16, fy, 3.5);
      disc(ctx, CUT_BLUE[1], 16, fy, 2.5);
      fill(ctx, CUT_BLUE[2], 15, fy - 1, 1, 1);
    },
  },
];

const roots: PlantDesign[] = [
  {
    family: "natural",
    blurb: "Three brown roots wavering down the tile, lit along one side, with rootlets branching off and crumbs of soil where they leave the rock.",
    paint: (ctx, v) => {
      [6, 16, 26].forEach((x, i) => {
        const pts = strand(x, 2.5, v * 1.7 + i * 2.1);
        path(ctx, EARTH[1], pts, 3);
        path(ctx, EARTH[3], pts.map((p, k) => (k % 2 ? p : p - 0.8)), 1);
        const ry = 10 + ((i * 7 + v * 5) % 14);
        const rx = strandX(pts, ry);
        line(ctx, EARTH[1], rx, ry, rx + (i === 2 ? -5 : 5), ry + 5, 1.2);
        if (v === i) for (const [dx, dy] of [[-2, 0], [2, 1], [0, 2]]) fill(ctx, EARTH[0], x + dx!, dy!, 1, 1);
      });
    },
  },
  {
    family: "otherworld",
    blurb: "Pale indigo root threads with a teal vein down each, swelling into nodules that glow faintly.",
    paint: (ctx, v) => {
      [10, 22].forEach((x, i) => {
        const pts = strand(x, 2, v * 2.3 + i * 1.3);
        path(ctx, INDIGO[3], pts, 3);
        path(ctx, INDIGO[5], pts.map((p, k) => (k % 2 ? p : p - 0.7)), 1);
        pixels(ctx, S, S, (px, py) => (Math.abs(px + 0.5 - strandX(pts, py)) < 0.6 && py % 4 !== 3 ? TEAL[4] : null));
        const ny = 8 + ((i * 11 + v * 9) % 16);
        disc(ctx, INDIGO[4], strandX(pts, ny), ny, 2.5);
        spark(ctx, Math.round(strandX(pts, ny)) - 1, ny - 1, TEAL[3]);
      });
    },
  },
  {
    family: "woodcut",
    blurb: "One thick flat root with ink edges, a heavy rootlet forking off it, the same width top and bottom so a column reads as one root.",
    paint: (ctx, v) => {
      const pts = strand(16, 3, v * 2.1, 1);
      path(ctx, INK, pts, 7);
      path(ctx, CUT_BROWN[1], pts, 5);
      path(ctx, CUT_BROWN[2], pts.map((p, k) => (k % 2 ? p : p - 1.5)), 1.2);
      const ry = [10, 18, 6][v]!;
      const side = v === 1 ? -1 : 1;
      const rx = strandX(pts, ry);
      line(ctx, INK, rx, ry, rx + side * 9, ry + 9, 4);
      line(ctx, CUT_BROWN[1], rx, ry, rx + side * 8, ry + 8, 2.2);
    },
  },
];

const vine: PlantDesign[] = [
  {
    family: "natural",
    blurb: "Two green strands twisting down the tile, heart-shaped leaves alternating sides; each variant shifts the twist so a long vine doesn't repeat.",
    paint: (ctx, v) => {
      [9, 22].forEach((x, i) => {
        const pts = strand(x, 2.5, v * 2.1 + i * 2.6);
        path(ctx, GREEN[1], pts, 2);
        path(ctx, GREEN[3], pts.map((p, k) => (k % 2 ? p : p - 0.6)), 1);
        for (const ly of [5 + ((v * 4 + i * 6) % 7), 17 + ((v * 5 + i * 3) % 7)]) {
          const side = Math.floor(ly / 11) % 2 ? 1 : -1;
          const lx = strandX(pts, ly);
          leaf(ctx, lx + side * 4, ly, 4, 2.4, side * 0.5, BUSH);
          fill(ctx, GREEN[0], Math.round(lx + side * 6) - (side > 0 ? 0 : 1), ly - 2, 1, 1);
        }
      });
    },
  },
  {
    family: "otherworld",
    blurb: "A dark tendril strung with glowing teal beads, seed pods hanging off it with a lit tip.",
    paint: (ctx, v) => {
      const pts = strand(14, 3, v * 1.9);
      path(ctx, INDIGO[2], pts, 2);
      path(ctx, INDIGO[4], pts.map((p, k) => (k % 2 ? p : p - 0.6)), 1);
      for (let y = 3 + v * 2; y < S; y += 7) {
        disc(ctx, TEAL[2], strandX(pts, y), y, 2);
        fill(ctx, TEAL[5], Math.round(strandX(pts, y)) - 1, y - 1, 1, 1);
      }
      for (const py of [[9, 24], [13, 28], [6, 20]][v]!) {
        const px = strandX(pts, py);
        const side = py > 16 ? -1 : 1;
        line(ctx, INDIGO[3], px, py, px + side * 5, py + 3, 1);
        oval(ctx, TEAL[1], px + side * 6, py + 6, 2, 4);
        oval(ctx, TEAL[3], px + side * 6 - 0.5, py + 5, 1, 2.5);
        spark(ctx, Math.round(px + side * 6), py + 10, TEAL[3]);
      }
    },
  },
  {
    family: "woodcut",
    blurb: "A rope-thick flat vine with ink edges, two big flat leaves, and a curling ink tendril on one variant.",
    paint: (ctx, v) => {
      const pts = strand(15, 2.5, v * 2.4);
      path(ctx, INK, pts, 5);
      path(ctx, CUT[1], pts, 3);
      path(ctx, CUT[2], pts.map((p, k) => (k % 2 ? p : p - 1)), 1);
      for (const [ly, side] of [[[8, -1], [22, 1]], [[12, 1], [27, -1]], [[5, 1], [18, -1]]][v]!) {
        const lx = strandX(pts, ly!);
        cutLeaf(ctx, lx + side! * 6, ly!, 6, 3.5, side! * 0.55, CUT);
      }
      if (v === 1) {
        const tx = strandX(pts, 20) - 3;
        path(ctx, INK, [tx, 20, tx - 3, 22, tx - 5, 26, tx - 3, 29, tx, 28], 1);
      }
    },
  },
];

const crystal: PlantDesign[] = [
  {
    family: "natural",
    blurb: "Amethyst: a big six-sided shard with a lit face and a bright edge, two smaller shards leaning out beside it, white glints.",
    paint: (ctx, v) => {
      const lean = [0, 2, -2][v]!;
      shard(ctx, 6 - lean, 14, 10, S, 3, 0.4, VIOLET);
      shard(ctx, 27 + lean, 10, 23, S, 3.5, 0.4, VIOLET);
      shard(ctx, 16 + lean, 1, 16, S, 6, 0.45, VIOLET);
      fill(ctx, "#ffffff", 13 + lean, 9, 2, 2);
      fill(ctx, "#ffffff", 25 + lean, 15, 1, 1);
    },
  },
  {
    family: "otherworld",
    blurb: "Void crystal: near-black glassy shards cracked with glowing violet veins and a lit core, as if the light were inside.",
    paint: (ctx, v) => {
      const lean = [0, 2, -2][v]!;
      const dark = ["#120d22", "#1a1430", "#261c48", "#38296a", "#6a58a8"] as const;
      shard(ctx, 6 - lean, 11, 9, S, 4, 0.4, dark);
      shard(ctx, 27 + lean, 8, 24, S, 4.5, 0.4, dark);
      shard(ctx, 16 + lean, 1, 16, S, 8, 0.45, dark);
      path(ctx, VIOLET[4], [16 + lean, 5, 15 + lean, 12, 18 + lean, 17, 16 + lean, 26], 1);
      line(ctx, VIOLET[4], 15 + lean, 12, 11 + lean, 16, 1);
      line(ctx, VIOLET[3], 18 + lean, 17, 21 + lean, 22, 1);
      line(ctx, VIOLET[3], 26 + lean, 13, 25 + lean, 24, 1);
      line(ctx, VIOLET[3], 7 - lean, 16, 8 - lean, 26, 1);
      disc(ctx, VIOLET[3], 16 + lean, 18, 3.2);
      disc(ctx, VIOLET[4], 16 + lean, 18, 2.2);
      disc(ctx, VIOLET[5], 16 + lean, 18, 1);
    },
  },
  {
    family: "woodcut",
    blurb: "Three flat violet shards with ink edges and one bright stripe each: bold and geometric.",
    paint: (ctx, v) => {
      const lean = [0, 2, -2][v]!;
      const cut = (tx: number, ty: number, bx: number, half: number) => {
        poly(ctx, INK, [tx, ty - 1, bx + half + 1, (ty + S) / 2, bx + half + 1, S, bx - half - 1, S, bx - half - 1, (ty + S) / 2]);
        poly(ctx, CUT_VIOLET[1], [tx, ty, bx + half, (ty + S) / 2, bx + half, S, bx - half, S, bx - half, (ty + S) / 2]);
        poly(ctx, CUT_VIOLET[0], [tx, ty, bx + half, (ty + S) / 2, bx + half, S, bx, S, bx, (ty + S) / 2 + 2]);
        line(ctx, CUT_VIOLET[2], tx - 0.5, ty + 2, bx - half + 1.5, (ty + S) / 2 + 2, 1.5);
        ink(ctx, tx, ty, bx, (ty + S) / 2 + 2);
      };
      cut(6 - lean, 13, 9, 3);
      cut(27 + lean, 10, 24, 3);
      cut(16 + lean, 1, 16, 6);
    },
  },
];

const sapling: PlantDesign[] = [
  {
    family: "natural",
    blurb: "A thin brown stem with two pairs of round leaves and a pale bud at the tip.",
    paint: (ctx) => {
      stem(ctx, 15, 9, S - 9, 2, EARTH);
      leaf(ctx, 10, 22, 5, 2.5, -0.5, BUSH);
      leaf(ctx, 22, 20, 5, 2.5, 0.5, BUSH);
      leaf(ctx, 11, 13, 4.5, 2.2, -0.6, BUSH);
      leaf(ctx, 21, 12, 4.5, 2.2, 0.6, BUSH);
      oval(ctx, GREEN[3], 16, 7, 2, 3);
      oval(ctx, GREEN[5], 15.5, 6, 1, 1.6);
    },
  },
  {
    family: "otherworld",
    blurb: "A young starbark: indigo stem, three long teal fronds lifting upward, and a glowing tip that will be its first bulb.",
    paint: (ctx) => {
      stem(ctx, 15, 8, S - 8, 2, INDIGO);
      fill(ctx, INDIGO[5], 15, 14, 2, 1);
      fill(ctx, INDIGO[5], 15, 22, 2, 1);
      leaf(ctx, 9, 15, 7, 2.2, 0.75, TEAL);
      leaf(ctx, 23, 13, 7, 2.2, -0.75, TEAL);
      leaf(ctx, 11, 24, 6, 2, 0.55, TEAL);
      disc(ctx, TEAL[3], 16, 5, 3);
      disc(ctx, TEAL[5], 16, 5, 1.8);
      fill(ctx, GLOW_PX, 15, 4, 1, 1);
    },
  },
  {
    family: "woodcut",
    blurb: "A stout stem in flat brown with ink edges and three big flat leaves.",
    paint: (ctx) => {
      fill(ctx, INK, 14, 10, 5, S - 10);
      fill(ctx, CUT_BROWN[1], 15, 10, 3, S - 10);
      fill(ctx, CUT_BROWN[2], 15, 10, 1, S - 10);
      cutLeaf(ctx, 9, 20, 6.5, 3.5, -0.6, CUT);
      cutLeaf(ctx, 23, 17, 6.5, 3.5, 0.6, CUT);
      cutLeaf(ctx, 16, 7, 4, 6, 0.1, CUT);
    },
  },
];

// ----- new kinds: plants the game doesn't grow yet

const FIRE = ["#4a1206", "#9a2a0a", "#e05a14", "#ff9a2a", "#ffd25a", "#fff4c0"] as const;
const LICHEN = ["#3a3c2c", "#6a6e52", "#8c9270", "#aab08a", "#c8cca6"] as const;
const PURPLE = ["#3a1a5a", "#6a3aa0", "#9a5ad0", "#c48af0", "#e8d0ff"] as const;

/** A jagged thistle leaf out from (x, y) to one side: a spined blade. */
function spinyLeaf(ctx: Ctx, x: number, y: number, side: number, ramp: Ramp, inkEdge = false): void {
  const pts = [x, y, x + side * 4, y - 1, x + side * 8, y - 5, x + side * 11, y - 10];
  if (inkEdge) path(ctx, INK, pts, 4);
  path(ctx, ramp[1]!, pts, 2.2);
  path(ctx, ramp[3]!, pts.map((p, k) => (k % 2 ? p - 0.7 : p)), 1);
  for (let k = 0; k < 3; k++) {
    const px = x + side * (3 + k * 3);
    const py = y - 1 - k * 3;
    line(ctx, inkEdge ? INK : ramp[2]!, px, py, px + side * 1.5, py - 3, 1);
    line(ctx, inkEdge ? INK : ramp[2]!, px, py + 1, px + side * 2, py + 3, 1);
  }
}

const thistle: PlantDesign[] = [
  {
    family: "natural",
    blurb: "A spined green stem the height of the tile with jagged leaves, one variant topped by a purple tufted head. Meadows and groves, two tiles tall.",
    paint: (ctx, v) => {
      stem(ctx, 15, 0, S, 2, GREEN);
      const ly = [22, 14, 26][v]!;
      spinyLeaf(ctx, 16, ly, -1, GREEN);
      spinyLeaf(ctx, 16, ly - 6, 1, GREEN);
      if (v === 1) {
        for (let i = 0; i < 9; i++) {
          const a = Math.PI + (i / 8) * Math.PI;
          line(ctx, i % 2 ? PURPLE[2] : PURPLE[3], 16, 9, 16 + Math.cos(a) * 6, 9 + Math.sin(a) * 7, 1.2);
        }
        oval(ctx, GREEN[1], 16, 11, 4, 3.5);
        oval(ctx, GREEN[3], 15, 10, 2, 1.5);
        for (const [dx, dy] of [[-2, 1], [1, 2], [2, -1]]) fill(ctx, GREEN[0], 16 + dx!, 11 + dy!, 1, 1);
      }
    },
  },
  {
    family: "otherworld",
    blurb: "An indigo stem with barbed teal leaves and a head that is a glowing violet puff shedding sparks.",
    paint: (ctx, v) => {
      stem(ctx, 15, 0, S, 2, INDIGO);
      const ly = [22, 14, 26][v]!;
      spinyLeaf(ctx, 16, ly, -1, TEAL);
      spinyLeaf(ctx, 16, ly - 6, 1, TEAL);
      if (v === 1) {
        disc(ctx, PURPLE[1], 16, 9, 5.5);
        disc(ctx, PURPLE[2], 15.5, 8.5, 4);
        disc(ctx, PURPLE[4], 15, 8, 1.8);
        for (const [x, y] of [[9, 4], [23, 6], [20, 1], [11, 12]]) spark(ctx, x!, y!, PURPLE[3]);
        oval(ctx, INDIGO[2], 16, 14, 3.5, 2.5);
      }
    },
  },
  {
    family: "woodcut",
    blurb: "A flat stem with ink barbs along the leaves and a bold purple head ringed in ink.",
    paint: (ctx, v) => {
      fill(ctx, INK, 14, 0, 4, S);
      fill(ctx, CUT[1], 15, 0, 2, S);
      const ly = [22, 14, 26][v]!;
      spinyLeaf(ctx, 16, ly, -1, CUT, true);
      spinyLeaf(ctx, 16, ly - 6, 1, CUT, true);
      if (v === 1) {
        disc(ctx, INK, 16, 8, 6);
        disc(ctx, PURPLE[2], 16, 8, 4.5);
        poly(ctx, PURPLE[4], [12, 8, 16, 3, 20, 8, 16, 7]);
        oval(ctx, INK, 16, 13, 4.5, 3.5);
        oval(ctx, CUT[1], 16, 13, 3, 2.3);
      }
    },
  },
];

const fern: PlantDesign[] = [
  {
    family: "natural",
    blurb: "Arching fronds with paired leaflets over a dark interior; the tiles make a mound of fern, bitten at the edges.",
    paint: (ctx, v) => {
      fill(ctx, BUSH[0]);
      const fronds = [[[2, 30, 1], [30, 28, -1], [10, 32, 1]], [[0, 26, 1], [28, 32, -1], [18, 30, 1]], [[4, 32, 1], [32, 24, -1], [14, 28, -1]]][v]!;
      for (const [x0, y0, side] of fronds) {
        const pts = [x0!, y0!, x0! + side! * 7, y0! - 9, x0! + side! * 13, y0! - 17, x0! + side! * 17, y0! - 26];
        path(ctx, GREEN[1], pts, 1.5);
        for (let k = 1; k < 8; k++) {
          const t = k / 8;
          const px = x0! + side! * 17 * t;
          const py = y0! - 26 * t + Math.sin(t * 3) * 1.5;
          const r = 3.5 * (1 - t * 0.6);
          oval(ctx, GREEN[2], px - side! * r * 0.3, py - r * 0.9, r * 0.6, r, side! * 0.6);
          oval(ctx, GREEN[3], px + side! * r * 0.5, py + r * 0.7, r * 0.6, r, -side! * 0.6);
          fill(ctx, GREEN[4], Math.round(px - side! * r * 0.5), Math.round(py - r), 1, 1);
        }
      }
      for (const [cx, cy, r] of [[[0, 0, 4], [S, 14, 3]], [[S, 0, 4], [8, S, 3]], [[14, 0, 3], [0, 20, 4]]][v]!) bite(ctx, cx!, cy!, r!);
    },
  },
  {
    family: "otherworld",
    blurb: "Teal fronds curling into fiddleheads with glowing tips, over indigo shade.",
    paint: (ctx, v) => {
      fill(ctx, INDIGO[0]);
      pixels(ctx, S, S, (x, y) => (noise(x, y, 6, 400 + v) > 0.6 ? INDIGO[1] : null));
      const fronds = [[[4, 32, 1], [30, 30, -1]], [[0, 28, 1], [26, 32, -1], [16, 32, 1]], [[8, 32, -1], [32, 26, -1], [20, 30, 1]]][v]!;
      for (const [x0, y0, side] of fronds) {
        const pts = [x0!, y0!, x0! + side! * 6, y0! - 10, x0! + side! * 10, y0! - 20, x0! + side! * 8, y0! - 26, x0! + side! * 4, y0! - 25];
        path(ctx, TEAL[1], pts, 3);
        path(ctx, TEAL[3], pts, 1.2);
        for (let k = 1; k < 6; k++) {
          const t = k / 6;
          const px = x0! + side! * 9 * t;
          const py = y0! - 20 * t;
          line(ctx, TEAL[2], px, py, px - side! * 4, py - 3, 1.5);
          line(ctx, TEAL[2], px, py, px + side! * 4, py - 1, 1.5);
        }
        spark(ctx, Math.round(x0! + side! * 4), y0! - 25, TEAL[4]);
      }
      for (const [cx, cy, r] of [[[0, 0, 4], [S, 12, 3]], [[S, 0, 3], [8, S, 4]], [[12, 0, 3], [0, 20, 4]]][v]!) bite(ctx, cx!, cy!, r!);
    },
  },
  {
    family: "woodcut",
    blurb: "Bold flat fronds, each leaflet a flat shape with an ink stem, notched at the tile's edges.",
    paint: (ctx, v) => {
      fill(ctx, CUT[0]);
      const fronds = [[[2, 32, 1], [30, 30, -1]], [[0, 26, 1], [28, 32, -1]], [[6, 32, 1], [32, 22, -1]]][v]!;
      for (const [x0, y0, side] of fronds) {
        const pts = [x0!, y0!, x0! + side! * 8, y0! - 10, x0! + side! * 15, y0! - 20, x0! + side! * 19, y0! - 28];
        for (let k = 1; k < 7; k++) {
          const t = k / 7;
          const px = x0! + side! * 19 * t;
          const py = y0! - 28 * t;
          const r = 4.5 * (1 - t * 0.5);
          cutLeaf(ctx, px - side! * r * 0.4, py - r * 0.8, r, r * 0.45, side! * 1.1, CUT);
          cutLeaf(ctx, px + side! * r * 0.6, py + r * 0.5, r, r * 0.45, -side! * 1.1 + Math.PI, CUT);
        }
        path(ctx, INK, pts, 2);
      }
      ctx.save();
      ctx.globalCompositeOperation = "destination-out";
      for (const [x, y, dx, dy] of [[[0, 0, 5, 7], [S, 10, -6, 5]], [[S, 0, -7, 5], [8, S, 6, -5]], [[14, 0, 6, 5], [0, 22, 6, 5]]][v]!) poly(ctx, "#000", [x!, y!, x! + dx!, y!, x!, y! + dy!]);
      ctx.restore();
    },
  },
];

const frostGrass: PlantDesign[] = [
  {
    family: "natural",
    blurb: "Brittle pale blades rimed white, ice crystals at their tips; two or three tiles tall on frost rocks.",
    paint: (ctx, v) => {
      const xs = [[5, 14, 24], [9, 18, 27], [3, 12, 22]][v]!;
      xs.forEach((x, i) => {
        const l = [1, -1, 2][(i + v) % 3]!;
        line(ctx, ICE[1], x, S, x + l, 0, 2.5);
        line(ctx, ICE[3], x - 0.8, S, x - 0.8 + l, 0, 1);
        const side = (i + v) % 2 ? 1 : -1;
        line(ctx, ICE[2], x, 26 - i * 4, x + side * 6, 12 - i * 3, 1.5);
        line(ctx, ICE[4], x + side * 6, 12 - i * 3, x + side * 7, 9 - i * 3, 1);
        if ((i + v) % 3 === 0) {
          fill(ctx, ICE[5], x + l - 1, 1, 3, 1);
          fill(ctx, ICE[5], x + l, 0, 1, 3);
        }
      });
    },
  },
  {
    family: "otherworld",
    blurb: "Blades of translucent ice prism, sharp-edged, lit at the root as if the frost itself glowed.",
    paint: (ctx, v) => {
      const xs = [[6, 16, 26], [10, 20, 29], [3, 13, 23]][v]!;
      xs.forEach((x, i) => {
        const l = [2, -2, 3][(i + v) % 3]!;
        poly(ctx, ICE[2], [x - 2, S, x + 2, S, x + l + 0.5, 0, x + l - 0.5, 0]);
        poly(ctx, ICE[4], [x - 2, S, x, S, x + l, 0, x + l - 0.5, 0]);
        line(ctx, ICE[5], x - 1.5, S, x + l - 0.5, 1, 1);
        const side = (i + v) % 2 ? 1 : -1;
        poly(ctx, ICE[3], [x, 24 - i * 3, x + side * 7, 14 - i * 3, x + side * 1.5, 22 - i * 3]);
        if (v === i) spark(ctx, x, S - 4, ICE[3]);
      });
    },
  },
  {
    family: "woodcut",
    blurb: "Flat white-and-blue blades with ink edges, a sharp ink crystal on one tip.",
    paint: (ctx, v) => {
      const xs = [[6, 17, 27], [4, 15, 25], [9, 20, 30]][v]!;
      xs.forEach((x, i) => {
        const l = [1, -1, 2][(i + v) % 3]!;
        line(ctx, INK, x, S, x + l, 0, 4);
        line(ctx, ICE[3], x, S, x + l, 0, 2.2);
        line(ctx, ICE[5], x - 0.8, S, x - 0.8 + l, 0, 0.9);
        const side = (i + v) % 2 ? 1 : -1;
        line(ctx, INK, x, 26 - i * 4, x + side * 7, 13 - i * 3, 3);
        line(ctx, ICE[3], x, 26 - i * 4, x + side * 7, 13 - i * 3, 1.4);
        if ((i + v) % 3 === 1) {
          poly(ctx, INK, [x + l - 3, 5, x + l + 3, 5, x + l, 0]);
          poly(ctx, ICE[5], [x + l - 1.5, 4, x + l + 1.5, 4, x + l, 1]);
        }
      });
    },
  },
];

const cinderBloom: PlantDesign[] = [
  {
    family: "natural",
    blurb: "A flower on the ash rocks near the suns: a charcoal stem and curled petals that glow like embers, shedding sparks. Lights its patch of ground.",
    paint: (ctx, v) => {
      const fy = [12, 9, 16][v]!;
      stem(ctx, 15, fy, S - fy, 2, ["#1a1412", "#2e2622", "#4a3e36"]);
      leaf(ctx, 10 + (v === 1 ? 12 : 0), fy + 11, 4.5, 2, v === 1 ? 0.5 : -0.5, ["#1a1412", "#2e2622", "#4a3e36", "#6a5a50"]);
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * Math.PI * 2 + v * 0.4;
        oval(ctx, FIRE[1], 16 + Math.cos(a) * 6, fy + Math.sin(a) * 6, 4.5, 2.6, a);
        oval(ctx, FIRE[2], 16 + Math.cos(a) * 5.5, fy + Math.sin(a) * 5.5, 3.2, 1.6, a);
        oval(ctx, FIRE[3], 16 + Math.cos(a) * 4, fy + Math.sin(a) * 4, 1.8, 1, a);
      }
      disc(ctx, FIRE[3], 16, fy, 3);
      disc(ctx, FIRE[4], 16, fy, 2);
      fill(ctx, FIRE[5], 15, fy - 1, 1, 1);
      for (const [x, y] of [[8, 3], [25, 5], [22, 1]]) spark(ctx, x!, y! + (v === 1 ? 0 : 3), FIRE[3]);
    },
  },
  {
    family: "otherworld",
    blurb: "Petals that are tongues of flame around a white-hot core, on a stem of black glass, smoke beads rising.",
    paint: (ctx, v) => {
      const fy = [13, 9, 16][v]!;
      stem(ctx, 15, fy, S - fy, 2, ["#0b0a10", "#1c1a26", "#3a3650"]);
      pixels(ctx, 1, S - fy, (_, y) => (y % 4 === 0 ? "#6a6a80" : null), 15, fy);
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2 - Math.PI / 2 + v * 0.3;
        const tx = 16 + Math.cos(a) * 10;
        const ty = fy + Math.sin(a) * 10;
        const nx = -Math.sin(a) * 2.5;
        const ny = Math.cos(a) * 2.5;
        poly(ctx, FIRE[1], [16 + nx, fy + ny, tx + nx * 0.4, ty + ny * 0.4 - 2, tx, ty, 16 - nx, fy - ny]);
        poly(ctx, FIRE[2], [16 + nx * 0.6, fy + ny * 0.6, tx + nx * 0.2, ty + ny * 0.2 - 1, 16 - nx * 0.4, fy - ny * 0.4]);
        line(ctx, FIRE[3], 16, fy, tx * 0.6 + 16 * 0.4, ty * 0.6 + fy * 0.4, 1);
      }
      disc(ctx, FIRE[3], 16, fy, 3.5);
      disc(ctx, FIRE[5], 16, fy, 2);
      for (const [x, y] of [[9, 2], [24, 4], [13, 0]]) fill(ctx, "#5a5668", x!, y!, 2, 1);
    },
  },
  {
    family: "woodcut",
    blurb: "An ink stem and bold flame-shaped petals in three fire tones, each edged in ink, a yellow core.",
    paint: (ctx, v) => {
      const fy = [13, 9, 16][v]!;
      fill(ctx, INK, 14, fy, 4, S - fy);
      fill(ctx, "#3a2e2a", 15, fy, 2, S - fy);
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2 - Math.PI / 2 + v * 0.3;
        const tx = 16 + Math.cos(a) * 11;
        const ty = fy + Math.sin(a) * 11;
        const nx = -Math.sin(a) * 3.5;
        const ny = Math.cos(a) * 3.5;
        poly(ctx, INK, [16 + nx * 1.3, fy + ny * 1.3, tx, ty - 1.5, 16 - nx * 1.3, fy - ny * 1.3]);
        poly(ctx, FIRE[2], [16 + nx, fy + ny, tx * 0.92 + 16 * 0.08, ty * 0.92 + fy * 0.08, 16 - nx, fy - ny]);
        poly(ctx, FIRE[3], [16 + nx * 0.4, fy + ny * 0.4, tx * 0.6 + 16 * 0.4, ty * 0.6 + fy * 0.4, 16 - nx * 0.4, fy - ny * 0.4]);
      }
      disc(ctx, INK, 16, fy, 4);
      disc(ctx, FIRE[4], 16, fy, 2.8);
      fill(ctx, FIRE[5], 15, fy - 1, 1, 1);
    },
  },
];

const lichenFingers: PlantDesign[] = [
  {
    family: "natural",
    blurb: "Pale grey-green lichen fingers forking up from the rock, each tipped red like a match head. Lichen rocks, one or two tiles tall.",
    paint: (ctx, v) => {
      const bases = [[6, 16, 26], [10, 21], [4, 14, 23, 30]][v]!;
      bases.forEach((x, i) => {
        const h = 14 + ((i * 7 + v * 5) % 12);
        const lean = ((i + v) % 2 ? 1 : -1) * 2;
        path(ctx, LICHEN[1], [x, S, x + lean * 0.5, S - h * 0.5, x + lean, S - h], 4);
        path(ctx, LICHEN[3], [x - 1, S, x - 1 + lean * 0.5, S - h * 0.5, x - 1 + lean, S - h], 1.5);
        const fx = x + lean * 0.6;
        const fy = S - h * 0.6;
        line(ctx, LICHEN[1], fx, fy, fx - lean * 2 - 3, fy - 7, 3);
        line(ctx, LICHEN[3], fx - 0.5, fy, fx - lean * 2 - 3.5, fy - 7, 1);
        disc(ctx, "#b8321c", x + lean, S - h - 1, 2.2);
        disc(ctx, "#e05a3a", x + lean - 0.5, S - h - 1.5, 1.2);
        disc(ctx, "#b8321c", fx - lean * 2 - 3, fy - 8, 1.8);
      });
    },
  },
  {
    family: "otherworld",
    blurb: "Stone coral: grey branching fingers with teal light pooled in each tip, as if the rock had grown feelers.",
    paint: (ctx, v) => {
      const bases = [[7, 18, 27], [12, 23], [5, 15, 25]][v]!;
      bases.forEach((x, i) => {
        const h = 16 + ((i * 9 + v * 4) % 12);
        const lean = ((i + v) % 2 ? 1 : -1) * 3;
        path(ctx, LICHEN[0], [x, S, x + lean * 0.3, S - h * 0.5, x + lean, S - h], 5);
        path(ctx, LICHEN[2], [x, S, x + lean * 0.3, S - h * 0.5, x + lean, S - h], 3);
        path(ctx, LICHEN[4], [x - 1, S, x - 1 + lean * 0.3, S - h * 0.5, x - 1 + lean, S - h], 1);
        const fy = S - h * 0.55;
        line(ctx, LICHEN[2], x + lean * 0.3, fy, x - lean - 2, fy - 8, 2.5);
        for (const [tx, ty] of [[x + lean, S - h - 1], [x - lean - 2, fy - 9]]) {
          disc(ctx, TEAL[2], tx!, ty!, 2.5);
          disc(ctx, TEAL[4], tx!, ty!, 1.4);
          spark(ctx, Math.round(tx!), Math.round(ty!) - 1, TEAL[4]);
        }
      });
    },
  },
  {
    family: "woodcut",
    blurb: "Flat grey fingers with ink edges and bold red tip discs.",
    paint: (ctx, v) => {
      const bases = [[6, 17, 27], [11, 22], [4, 15, 25]][v]!;
      bases.forEach((x, i) => {
        const h = 14 + ((i * 7 + v * 5) % 12);
        const lean = ((i + v) % 2 ? 1 : -1) * 2;
        const pts = [x, S, x + lean * 0.5, S - h * 0.5, x + lean, S - h];
        path(ctx, INK, pts, 6);
        path(ctx, LICHEN[3], pts, 4);
        path(ctx, LICHEN[4], pts.map((p, k) => (k % 2 ? p : p - 1.2)), 1);
        const fx = x + lean * 0.6;
        const fy = S - h * 0.6;
        line(ctx, INK, fx, fy, fx - lean * 2 - 3, fy - 7, 4.5);
        line(ctx, LICHEN[3], fx, fy, fx - lean * 2 - 3, fy - 7, 2.5);
        for (const [tx, ty] of [[x + lean, S - h - 1], [fx - lean * 2 - 3, fy - 8]]) {
          disc(ctx, INK, tx!, ty!, 3.2);
          disc(ctx, "#c83a22", tx!, ty!, 2.2);
          fill(ctx, "#ff8a6a", Math.round(tx!) - 1, Math.round(ty!) - 1, 1, 1);
        }
      });
    },
  },
];

const glowDrops: PlantDesign[] = [
  {
    family: "natural",
    blurb: "Fine threads hanging from a cave roof, strung with glowing blue-green beads like glow-worm snares. Two to four tiles, lighting the cave.",
    paint: (ctx, v) => {
      [6, 13, 20, 27].forEach((x, i) => {
        const pts = strand(x, 1, v * 1.3 + i * 2);
        path(ctx, CREAM[0], pts, 1);
        for (let y = 3 + ((i * 5 + v * 3) % 7); y < S; y += 8 + (i % 2) * 3) {
          const bx = strandX(pts, y);
          disc(ctx, CAPBLUE[1], bx, y, 1.8);
          disc(ctx, CAPBLUE[3], bx - 0.3, y - 0.3, 1);
          fill(ctx, GLOW_PX, Math.round(bx) - 1, y - 1, 1, 1);
        }
      });
    },
  },
  {
    family: "otherworld",
    blurb: "Indigo tendrils with teal teardrops swelling along them, each drop lit from inside, the lowest about to fall.",
    paint: (ctx, v) => {
      [9, 22].forEach((x, i) => {
        const pts = strand(x, 2, v * 2.1 + i * 1.7);
        path(ctx, INDIGO[2], pts, 2);
        path(ctx, INDIGO[4], pts.map((p, k) => (k % 2 ? p : p - 0.6)), 1);
        for (const dy of [[6, 20], [12, 27], [3, 17]][v]!) {
          const y = dy! + i * 2;
          const bx = strandX(pts, y);
          poly(ctx, TEAL[2], [bx, y - 1, bx + 2.5, y + 3, bx, y + 6, bx - 2.5, y + 3]);
          disc(ctx, TEAL[3], bx, y + 3.5, 2.2);
          disc(ctx, TEAL[5], bx - 0.4, y + 3, 1);
          spark(ctx, Math.round(bx), y + 8, TEAL[3]);
        }
      });
    },
  },
  {
    family: "woodcut",
    blurb: "Ink threads with flat bright drops, each a bold teardrop with a white glint.",
    paint: (ctx, v) => {
      [8, 17, 26].forEach((x, i) => {
        const pts = strand(x, 1.5, v * 1.9 + i * 2.2);
        path(ctx, INK, pts, 1.5);
        for (const dy of [[5, 21], [11, 26], [2, 16]][v]!) {
          const y = dy! + i * 3;
          const bx = strandX(pts, y);
          poly(ctx, INK, [bx, y - 2, bx + 3.5, y + 3, bx, y + 7.5, bx - 3.5, y + 3]);
          poly(ctx, CUT_BLUE[1], [bx, y - 0.5, bx + 2.2, y + 3, bx, y + 6, bx - 2.2, y + 3]);
          fill(ctx, CUT_BLUE[2], Math.round(bx) - 1, y + 2, 1, 2);
        }
      });
    },
  },
];

export const PLANT_DESIGNS: PlantGroup[] = [
  {
    id: "reeds", title: "Reeds", key: "REEDS",
    note: "Grow where it's wet: four to seven stalks side by side, each three to six tiles tall, so a tile must join the one above it. Shown as a tile's three variants, and a clump as the game lays it.",
    designs: reeds,
  },
  {
    id: "shrub", title: "Shrub", key: "SHRUB",
    note: "A round blob of tiles a few across, in most biomes. The tiles meet on every side, so the look is a texture with a ragged edge.",
    designs: shrub,
  },
  {
    id: "glow-fungus", title: "Glow fungus", key: "GLOW_FUNGUS",
    note: "One to three clusters on a cave floor, each one or two tiles tall. It lights the cave (GridMesh adds the glow).",
    designs: glowFungus,
  },
  {
    id: "frost-flower", title: "Frost flower", key: "FROST_FLOWER",
    note: "A stalk two or three tiles tall on frost rocks: the tiles stack into one spike.",
    designs: frostFlower,
  },
  {
    id: "roots", title: "Roots", key: "ROOTS",
    note: "Hang two to four tiles from a cave roof under the plants above. Drawn from the tile's top down.",
    designs: roots,
  },
  {
    id: "vine", title: "Hanging vine", key: "HANGING_VINE",
    note: "Hangs three to eight tiles from a cave roof and is climbed like a ladder. Drawn from the tile's top down.",
    designs: vine,
  },
  {
    id: "crystal", title: "Void crystal", key: "CRYSTAL",
    note: "Not a plant but grown like one: a fan of thick strokes, two and four tiles wide, out of the ground at home. Dug like rock; glows.",
    designs: crystal,
  },
  {
    id: "sapling", title: "Sapling", key: "SAPLING",
    note: "Planted by hand on grass or moss: one tile, whole.",
    designs: sapling,
  },
  {
    id: "thistle", title: "Sky thistle", key: "THISTLE",
    note: "Meadows: one or two spined stalks two or three tiles tall, each with a purple head. Drops fibre.",
    designs: thistle,
  },
  {
    id: "fern", title: "Fern", key: "FERN",
    note: "Groves and the shade under trees: a mound of fronds laid as a blob like a shrub. Drops fibre.",
    designs: fern,
  },
  {
    id: "frost-grass", title: "Frost grass", key: "FROST_GRASS",
    note: "Frost rocks: brittle pale blades two or three tiles tall in clumps like reeds. Drops ice.",
    designs: frostGrass,
  },
  {
    id: "cinder-bloom", title: "Cinder bloom", key: "CINDER_BLOOM",
    note: "Ash rocks near the suns, among the ember trees: a flower whose petals glow like embers, one or two tiles tall, a light source. Drops coal, and sometimes ember ore.",
    designs: cinderBloom,
  },
  {
    id: "lichen-fingers", title: "Lichen fingers", key: "LICHEN_FINGERS",
    note: "Lichen rocks: branching fingers one or two tiles tall tipped red, laid as glow fungi are. Drops lichen.",
    designs: lichenFingers,
  },
  {
    id: "glow-drops", title: "Glow drops", key: "GLOW_DROPS",
    note: "Cave roofs in dark and twilight rocks: threads strung with glowing beads hanging two to four tiles, a light where vines would hang. Drops glowcap.",
    designs: glowDrops,
  },
];

/** Every plant block's painter, by its name in the registry (NATURAL_REEDS ...). */
export const PLANT_PAINT: Readonly<Record<string, (ctx: Ctx, v: number) => void>> = Object.fromEntries(
  PLANT_DESIGNS.flatMap((g) => g.designs.map((d) => [`${d.family.toUpperCase()}_${g.key}`, d.paint])),
);
